import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Vacancy, dates, descriptions, locations, radii, remoteOptions, titles } from './mock-data.component';
import { SearchIndexService } from './services/search-index.service';
import { SharedService } from './shared.service';
import { Utils, industryOptions, educationalLevels, employmentTypes, salaryTypes, hourTypes, locationKeywords, searchKeywords } from './utils';
import { JobCatsOne } from './vacancy-data.component';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SeoService } from './services/seo.service';
import { environment } from 'client/environments/environment';
import { AnalyticsService } from './services/analytics.service';
import { MatPaginator } from '@angular/material/paginator';
import { ApplePaginatorEvent } from './components/apple-paginator.component';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'vacatures', // updated to Dutch; component class name retained
    templateUrl: './vacancies.component.html',
    styleUrls: ['./vacancies.component.scss'],
    standalone: false
})
export class VacanciesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("filterRef") filterRef: ElementRef
  @ViewChild("paginatorRef") paginatorRef: ElementRef
  @ViewChild("spinnerRef") spinnerRef: ElementRef
  @ViewChild("jobNotFoundRef") jobNotFoundRef: ElementRef
  @ViewChild(MatPaginator) paginator!: MatPaginator
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ViewChildren("vacanciesRef") vacanciesRef: QueryList<ElementRef>

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    // Force blur on page-size-select when clicking outside (apple-multiselect handles its own blur)
    const target = event.target as HTMLElement;
    const filterDropdowns = document.querySelectorAll('.page-size-select');
    
    filterDropdowns.forEach(dropdown => {
      if (dropdown !== target && !dropdown.contains(target)) {
        (dropdown as HTMLElement).blur();
      }
    });
  }

  @HostListener('document:touchend', ['$event'])
  onDocumentTouchEnd(event: Event) {
    // Additional touch handling for mobile - force blur immediately
    const target = event.target as HTMLElement;
    const filterDropdowns = document.querySelectorAll('.page-size-select');
    
    // Use setTimeout to ensure this runs after the browser's default touch handling
    setTimeout(() => {
      filterDropdowns.forEach(dropdown => {
        if (dropdown !== target && !dropdown.contains(target)) {
          (dropdown as HTMLElement).blur();
        }
      });
    }, 10);
  }

  public isMobileMenuHidden = true
  public allVacancies: Vacancy[] = []
  public displayedVacancies: Vacancy[] = []
  public displayedJobsCatsOne: JobCatsOne[] = []
  public filteredVacancies: Vacancy[] = []
  public filteredJobsCatsOne: JobCatsOne[] = []
  public pageSizeOptions = [5, 10, 20, 40]
  public pageSize = this.pageSizeOptions[1]
  public pageIndex = 0
  public length: number
  public form: UntypedFormGroup
  public titles: string[]
  public descriptions: string[]
  public locations: string[]
  public radii: number[]
  public remoteOptions: string[]
  public educationalLevels: string[]
  public employmentTypes: string[]
  public salaryTypes: string[]
  public hourTypes: string[]
  public industryOptions: string[]
  public dates: string[]
  public isMobile = false
  public isFilterMenuHidden = true
  public isJobMenuHidden = true
  public selectedJob: JobCatsOne
  public isLoadingJobs = true
  public jobs: JobCatsOne[] = []
  public locationAutosuggest: string[] = []
  public searchKeywordAutosuggest: string[] = []
  public pageSizeNgFor: number[] = []
  public dateToDatePosted = Utils.dateToDatePosted
  public parseJobDescription = Utils.parseJobDescription
  public parseSalary = Utils.parseSalary
  public parseHours = Utils.parseHours
  public mapIndustries = Utils.mapIndustries

  public get totalPages(): number { return Math.ceil((this.length || 0) / (this.pageSize || 1)); }
  public get pageDots(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i); }

  public constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private httpClient: HttpClient,
    public sharedService: SharedService,
    private readonly searchIndex: SearchIndexService,
  private readonly seo: SeoService,
  private readonly analytics: AnalyticsService,
  private readonly cdr: ChangeDetectorRef,
    ) {
      // this.allVacancies = generateMockVacancies(1000)
      // this.length = this.allVacancies.length
      this.titles = titles
      this.descriptions = descriptions
      this.locations = locations
      this.radii = radii
      this.remoteOptions = remoteOptions
      this.educationalLevels = educationalLevels
      this.employmentTypes = employmentTypes
      this.industryOptions = industryOptions
      this.dates = dates
      this.salaryTypes = salaryTypes
      this.hourTypes = hourTypes
      this.pageSizeNgFor = new Array(this.pageSize)
  }

  public get radius(): number | null { return this.form.get("radius")?.value }
  public get remote(): string | null { return this.form.get("remote")?.value }
  public get educationalLevel(): string | null { return this.form.get("educationalLevel")?.value }
  public get jobType(): string | null { return this.form.get("jobType")?.value }
  public get industry(): string | null { return this.form.get("industry")?.value }
  public get datePosted(): string | null { return this.form.get("datePosted")?.value }
  public get profession(): string | null { return this.form.get("profession")?.value?.toLowerCase().trim() }
  public get location(): string | null { return this.form.get("location")?.value }
  public get countryCode(): string | null { return this.form.get("countryCode")?.value }
  public get dateCreated(): string | null { return this.form.get("dateCreated")?.value }
  public get dateModified(): string | null { return this.form.get("dateModified")?.value }
  public get description(): string | null { return this.form.get("description")?.value }
  public get id(): string | null { return this.form.get("id")?.value }
  public get city(): string | null { return this.form.get("city")?.value }
  public get postalCode(): string | null { return this.form.get("postalCode")?.value }
  public get state(): string | null { return this.form.get("state")?.value }
  public get title(): string | null { return this.form.get("title")?.value?.toLowerCase().trim() }
  public get salary(): string | null { return this.form.get("salary")?.value }
  public get education(): string | null { return this.form.get("education")?.value }
  public get employment(): string | null { return this.form.get("employment")?.value }
  public get hours(): string | null { return this.form.get("hours")?.value ?? [] }
  public get industries(): string | null { return this.form.get("industries")?.value ?? [] }
  
  public get noResultsFound() {
    return !this.isLoadingJobs && this.displayedJobsCatsOne.length === 0
  }

  public get searchHistory(): string[] {
    if(localStorage.getItem("history")) {
      const history = JSON.parse(localStorage.getItem("history") ?? '')
      let profession = history?.profession as Array<string> ?? []
      profession = profession.filter(item => item.trim().length > 0)
      return profession
    }
    return []
  }

  public getJobs() {
    this.httpClient.get<{ jobs: JobCatsOne[]}>(`${Utils.getApiUrl()}/vacancies`, {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      }),
    }).subscribe(response => {
      this.jobs = response.jobs
      this.length = this.jobs.length
      this.isLoadingJobs = false
      this.displayedJobsCatsOne = this.filteredJobsCatsOne = this.jobs
        .slice(0 * this.pageSize, 0 * this.pageSize + this.pageSize)
      this.restorePageFromQueryParam()
      this.filterJobs()

      // Build suggestion indexes from loaded jobs
      this.searchIndex.initialize(this.jobs, searchKeywords, locationKeywords)
      this.cdr.markForCheck()
    })
  }

  public educationOrNA(job: JobCatsOne) {
    return job.education.length > 0 ? job.education : "N/A"
  }

  public employmentOrNA(job: JobCatsOne) {
    return job.employment.length > 0 ? job.employment : "N/A"
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0)
    this.initFormCatsOne()
    this.restoreFiltersFromQueryParams()
    this.restorePageFromQueryParam()
    this.setupFilterUrlSync()
    this.getJobs()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private readonly destroy$ = new Subject<void>()
  private readonly filterUrlSync$ = new Subject<void>()

  private setupFilterUrlSync(): void {
    this.filterUrlSync$.pipe(
      debounceTime(250),
      takeUntil(this.destroy$),
    ).subscribe(() => this.writeFiltersToUrl())
  }

  /** Patch form from query params. Deep-linkable filter state. */
  private restoreFiltersFromQueryParams(): void {
    const qpm = this.activatedRoute.snapshot.queryParamMap
    const patch: Record<string, unknown> = {}

    const q = qpm.get('q')
    if (q) { patch['profession'] = q }

    const loc = qpm.get('location')
    if (loc) { patch['location'] = loc }

    const industriesRaw = qpm.get('industries')
    if (industriesRaw) {
      const parts = industriesRaw.split(',').map(s => s.trim()).filter(Boolean)
      if (parts.length) { patch['industries'] = parts }
    }

    const hoursRaw = qpm.get('hours')
    if (hoursRaw) {
      const parts = hoursRaw.split(',').map(s => s.trim()).filter(Boolean)
      if (parts.length) { patch['hours'] = parts }
    }

    const datePosted = qpm.get('datePosted')
    if (datePosted && this.dates.includes(datePosted)) {
      patch['datePosted'] = datePosted
    } else if (datePosted) {
      void this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { datePosted: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    }

    const education = qpm.get('education')
    if (education) { patch['education'] = education }

    const salary = qpm.get('salary')
    if (salary) { patch['salary'] = salary }

    const employment = qpm.get('employment')
    if (employment) { patch['employment'] = employment }

    if (Object.keys(patch).length > 0) {
      this.form.patchValue(patch, { emitEvent: false })
    }
  }

  /** Reconcile URL with current form state. Synchronous — bypass debounce on explicit clear. */
  private writeFiltersToUrl(): void {
    const strVal = (name: string): string =>
      (this.form.get(name)?.value ?? '').toString().trim()

    const listVal = (name: string): string[] => {
      const v = this.form.get(name)?.value
      return Array.isArray(v)
        ? v.filter((x: unknown) => typeof x === 'string' && x.trim().length > 0)
        : []
    }

    const profession = strVal('profession')
    const location = strVal('location')
    const datePosted = strVal('datePosted')
    const education = strVal('education')
    const salary = strVal('salary')
    const employment = strVal('employment')
    const industriesList = listVal('industries')
    const hoursList = listVal('hours')

    const page = this.pageIndex > 0 ? this.pageIndex + 1 : null

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        q: profession || null,
        location: location || null,
        industries: industriesList.length ? industriesList.join(',') : null,
        hours: hoursList.length ? hoursList.join(',') : null,
        datePosted: datePosted || null,
        education: education || null,
        salary: salary || null,
        employment: employment || null,
        page,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  public async ngAfterViewInit(): Promise<void> {
    await Utils.timeout()
    if(window.innerWidth <= 767 ) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }
    if(window.innerWidth >= 1024) {
      this.isFilterMenuHidden = true
    }

    this.vacanciesRef.changes.subscribe(i => {
      const vacancies: Array<ElementRef> = (this.vacanciesRef as any)._results
      for(const vacancy of vacancies) {
        const v: HTMLElement = vacancy.nativeElement
        if(v && v.classList.contains("scroll-fade")) {
          v.classList.remove("scroll-fade")
        }
      }
    })

    window.addEventListener("resize", () => {
      if(window.innerWidth >= 1024) {
        this.isMobileMenuHidden = true
        this.isFilterMenuHidden = true
      }
      if(window.innerWidth <= 767 ) {
        this.isMobile = true
      } else {
        this.isMobile = false
      }
      this.cdr.markForCheck()
    })

    window.addEventListener("scroll", () => {
      this.scrollFade()
    })
    window.addEventListener("keypress", (event) => {
      if(event.key === "Enter") {
        Utils.saveSearchHistory(this.profession ?? "")
        this.autocomplete?.closePanel?.()
      }
    })
    this.scrollFade()
    this.setupMobileSelectBlur()
  }

  private setupMobileSelectBlur(): void {
    // Add blur event listeners to page-size-select for immediate focus removal on mobile
    // (apple-multiselect components handle their own blur behavior)
    setTimeout(() => {
      const filterDropdowns = document.querySelectorAll('.page-size-select');
      
      filterDropdowns.forEach(dropdown => {
        dropdown.addEventListener('change', () => {
          // Force blur immediately after selection on mobile
          setTimeout(() => {
            (dropdown as HTMLElement).blur();
          }, 50);
        });
        
        dropdown.addEventListener('blur', () => {
          // Ensure focus styles are removed
          (dropdown as HTMLElement).style.removeProperty('background');
          (dropdown as HTMLElement).style.removeProperty('border-color');
          (dropdown as HTMLElement).style.removeProperty('box-shadow');
        });
      });
    }, 100);
  }

  public toggleFilterMenu(): void {
    this.isFilterMenuHidden = !this.isFilterMenuHidden
    this.sharedService.isMobileMenuButtonHidden = !this.sharedService.isMobileMenuButtonHidden
    // document.body.style.position = this.isFilterMenuHidden ? "" : "fixed"
  }

  public toggleJobMenu(job?: JobCatsOne): void {
    if(job) {
      this.selectedJob = job
    }
    this.isJobMenuHidden = !this.isJobMenuHidden
    this.sharedService.isMobileMenuButtonHidden = !this.sharedService.isMobileMenuButtonHidden
    document.body.style.overflow = this.isJobMenuHidden ? "auto" : "hidden"
  }

  public viewJob(event: MouseEvent, job: JobCatsOne) {
    event.preventDefault()
    this.sharedService.selectedJobId = job.id
    this.sharedService.filterData.profession = this.profession ?? ""
    this.sharedService.filterData.location = this.location ?? ""
    this.sharedService.filterData.datePosted = this.datePosted ?? ""
    this.sharedService.filterData.education = this.education ?? ""
    this.sharedService.filterData.salary = this.salary ?? ""
    this.sharedService.filterData.employment = this.employment ?? ""
    this.sharedService.filterData.hours = this.hours ?? ""
    this.sharedService.filterData.industries = this.industries ?? ""
    const slug = (job?.title || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'')
    this.router.navigate([`${slug}-${job.id}`], { relativeTo: this.activatedRoute })
  }

  public apply(event: MouseEvent) {
    event.preventDefault()
    this.analytics.trackOpenApplication();
    this.sharedService.selectedJobId = undefined as any
  this.router.navigate(['open-sollicitatie'])
  }

  public pageChange(event: ApplePaginatorEvent): void {
    this.pageIndex = event.pageIndex
    this.pageSize = event.pageSize
    this.displayedJobsCatsOne = this.filteredJobsCatsOne
      .slice(event.pageIndex * event.pageSize, (event.pageIndex * event.pageSize) + event.pageSize)
      .sort((a, b) =>  new Date(b.publicationDate).getTime() - new Date(a.publicationDate).getTime())
    this.updatePageQueryParam()
    this.emitPaginationSeo()
    window.scrollTo(0, 0)
  }

  public clearProfession(): void {
    this.form.patchValue({ profession: "" })
    this.writeFiltersToUrl()
  }

  public clearLocation(): void {
    this.form.patchValue({ location: "" })
    this.writeFiltersToUrl()
  }

  public clearAll(): void {
    this.form.patchValue({
      profession: "",
      location: "",
      datePosted: "",
      industry: "",
      education: "",
      employment: "",
      salary: "",
      hours: "",
      industries: "",
    })

    this.sharedService.resetFilterData()
    this.writeFiltersToUrl()
  }

  public showDivider(index: number): boolean {
    return index < this.searchHistory.length - 1
  }

  public setProfession(word: string): void {
    this.form.patchValue({ profession: word })
  }

  public search(): void {
    Utils.saveSearchHistory(this.profession ?? "")
    this.writeFiltersToUrl()
  }

  private scrollFade(): void {
    const filter = this.filterRef?.nativeElement as HTMLElement
    const vacancies: Array<ElementRef> = (this.vacanciesRef as any)._results
    const paginator = this.paginatorRef?.nativeElement as HTMLElement
    const spinner = this.spinnerRef?.nativeElement as HTMLElement
    const jobNotFound = this.jobNotFoundRef?.nativeElement as HTMLElement

    if(filter && filter.getBoundingClientRect().y < window.innerHeight && filter.classList.contains("scroll-fade")) {
      filter.classList.remove("scroll-fade")
    }

    for(const vacancy of vacancies) {
      const v: HTMLElement = vacancy.nativeElement
      if(v && v.getBoundingClientRect().y < (window.innerHeight + 400) && v.classList.contains("scroll-fade")) {
        v.classList.remove("scroll-fade")
      }
    }

    if(spinner && spinner.getBoundingClientRect().y < (window.innerHeight + 400) && spinner.classList.contains("scroll-fade")) {
      spinner.classList.remove("scroll-fade")
    }

    if(paginator && paginator.getBoundingClientRect().y < window.innerHeight && paginator.classList.contains("scroll-fade")) {
      paginator.classList.remove("scroll-fade")
    }

    if(jobNotFound && jobNotFound.getBoundingClientRect().y < window.innerHeight && jobNotFound.classList.contains("scroll-fade")) {
      jobNotFound.classList.remove("scroll-fade")
    }
  }

  public toggleMobileMenu(): void {
    this.isMobileMenuHidden = !this.isMobileMenuHidden
    document.body.style.position = this.isMobileMenuHidden ? "" : "fixed"
  }

  public routeTo(menuItem: string, isMobileMenu: boolean = false): void {
    setTimeout(() => {
      this.router.navigate([`${menuItem.toLowerCase()}`], { relativeTo: this.activatedRoute })
    }, this.sharedService.transitionDelay)
  }

  private filterJobs() {
    this.filteredJobsCatsOne = this.jobs.filter(vacancy => {
      if (this.profession && this.profession !== '') {
        if (!this.searchIndex.matchesTitle(vacancy.title, this.profession)) {
          return false
        }
      }

      if(this.location 
        && this.location !== ''
        && !vacancy.location.city.toLowerCase().replace(new RegExp("[- ]+"), "").includes(this.location.replace(new RegExp("[- ]+"), "").toLowerCase())
        && !vacancy.location.state.toLowerCase().replace(new RegExp("[- ]+"), "").includes(this.location.replace(new RegExp("[- ]+"), "").toLowerCase())
        && !vacancy.location.postalCode.trim().replace(new RegExp("[ ]+"), "").toLowerCase().includes(this.location.trim().replace(new RegExp("[ ]+"), "").toLowerCase()))
        {
        return false
      }
      if(this.industry && vacancy.industry.toLowerCase() !== this.industry.toLowerCase()) { 
        return false
      }
      if(this.education && vacancy.education?.toLowerCase() !== this.education?.toLowerCase()) { 
        return false
      }
      if(this.employment && vacancy.employment?.toLowerCase() !== this.employment?.toLowerCase()) { 
        return false
      }

      const salaryNumber = Utils.salaryToNumber(this.salary ?? '')
      if(vacancy.salary.static && !isNaN(Number(vacancy.salary.static))) {
        if(salaryNumber && (Number(vacancy.salary.static) > salaryNumber)) { 
          return false
        }
      }
      if(vacancy.salary.max) {
        if(salaryNumber && salaryNumber > (Number(vacancy.salary.max))) { 
          return false
        }
      }

      if(Array.isArray(this.hours) && this.hours.length > 0) {
        if(!this.hours.every(i => vacancy.hours.includes(i))) {
          return false
        }
      }

      if(Array.isArray(this.industries) && this.industries.length > 0) {
        let hasIndustry = false
        for(const i of this.industries) {
          if (vacancy.industries.some(j => j == i)) {
            hasIndustry = true
            break
          }
        }
        if (!hasIndustry) {
          return false
        }
      }

      if(this.datePosted === "Afgelopen 3 dagen" && new Date(vacancy.publicationDate) < new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 3))) {
        return false
      }

      if(this.datePosted === "Afgelopen 7 dagen" && new Date(vacancy.publicationDate) < new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 7))) {
        return false
      }

      if(this.datePosted === "Afgelopen 14 dagen" && new Date(vacancy.publicationDate) < new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 14))) {
        return false
      }

      if(this.datePosted === "Meer dan 14 dagen geleden" && new Date(vacancy.publicationDate) > new Date(new Date().getTime() - (1000 * 60 * 60 * 24 * 14))) {
        return false
      }

      return true
    })

    this.filteredJobsCatsOne = this.filteredJobsCatsOne.map(i => {
      let score = 0
      if(this.profession && i.title.toLowerCase().includes(this.profession)) {
        score++
      }
      return {...i, score}
    }).sort((a, b) => b.score - a.score)

    this.length = this.filteredJobsCatsOne.length
    this.displayedJobsCatsOne = this.filteredJobsCatsOne
      .slice(this.pageIndex * this.pageSize, this.pageIndex * this.pageSize + this.pageSize)
    this.emitPaginationSeo()
  }

  private emitPaginationSeo(): void {
    const { prevUrl, nextUrl } = this.buildPrevNextLinks()
    this.seo.setPrevNext(prevUrl, nextUrl)
  }

  private initFormCatsOne(): void {
    this.form = this.formBuilder.group({
      industry: this.formBuilder.control(""),
      countryCode: this.formBuilder.control(""),
      dateCreated: this.formBuilder.control(""),
      dateModified: this.formBuilder.control(""),
      description: this.formBuilder.control(""),
      id: this.formBuilder.control(""),
      city: this.formBuilder.control(""),
      postalCode: this.formBuilder.control(""),
      state: this.formBuilder.control(""),
      title: this.formBuilder.control(""),
      salary: this.formBuilder.control(this.sharedService?.filterData?.salary ?? ""),
      profession: this.formBuilder.control(this.sharedService?.filterData?.profession ?? ""),
      location: this.formBuilder.control(this.sharedService?.filterData?.location ?? ""),
      jobType: this.formBuilder.control(""),
      education: this.formBuilder.control(this.sharedService?.filterData?.education ?? ""),
      employment: this.formBuilder.control(this.sharedService?.filterData?.employment ?? ""),
      hours: this.formBuilder.control(this.sharedService?.filterData?.hours ?? ""),
      industries: this.formBuilder.control(this.sharedService?.filterData?.industries ?? ""),
      radius: this.formBuilder.control(0),
      datePosted: this.formBuilder.control(this.sharedService?.filterData?.datePosted ?? ""),
    })

    this.form.valueChanges.subscribe(_ => {
      // Reset pagination to first page when filters/search change
      this.pageIndex = 0
      if (this.paginator) {
        this.paginator.firstPage()
      }
      this.filterUrlSync$.next()
      this.filterJobs()
    })
  }

  private restorePageFromQueryParam(): void {
    const pageParam = this.activatedRoute.snapshot.queryParamMap.get('page')
    if (pageParam) {
      const parsed = parseInt(pageParam, 10)
      if (!isNaN(parsed) && parsed >= 1) {
        this.pageIndex = parsed - 1 // query param is 1-based, pageIndex is 0-based
      }
    }
  }

  private updatePageQueryParam(): void {
    const page = this.pageIndex > 0 ? this.pageIndex + 1 : null
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  private buildPrevNextLinks(): { prevUrl?: string; nextUrl?: string } {
    const totalPages = this.totalPages
    const current = this.pageIndex + 1 // 1-based
    const base = (environment.siteUrl?.replace(/\/$/, '') || window.location.origin)
    const mk = (p: number) => `${base}/vacatures?page=${p}`
    const prevUrl = current > 1 ? mk(current - 1) : undefined
    const nextUrl = current < totalPages ? mk(current + 1) : undefined
    return { prevUrl, nextUrl }
  }

}
